/**
 * CategoryDrillDown — modal pokazujący transakcje filtrowane po wybranej kategorii
 * w danym zakresie. Member breakdown (kto wydał ile) + lista pojedynczych tx.
 */
import { useMemo } from 'react';
import { Modal } from '../ui/Modal';
import { TransactionRow } from '../dashboard/TransactionRow';
import { type Transaction } from '../../features/transactions/transactionsStore';
import { type Category } from '../../features/categories/categoriesStore';
import { useAuthStore } from '../../features/auth/authStore';
import { useWalletsStore } from '../../features/wallets/walletsStore';
import { formatCurrency, safeColor } from '../../lib/utils';
import { getCategoryIcon } from '../../lib/categoryIcons';

interface Props {
  category: Category | null; // jeśli null → wybierz "Inne"
  transactions: Transaction[]; // już przefiltrowane do range + walletu
  onClose: () => void;
  onPickTx?: (tx: Transaction) => void;
}

export function CategoryDrillDown({ category, transactions, onClose, onPickTx }: Props) {
  const family = useAuthStore((s) => s.family);
  const wallets = useWalletsStore((s) => s.wallets);
  const walletNamesById = useMemo(
    () => Object.fromEntries(wallets.map((w) => [w.id, w.name])),
    [wallets],
  );

  const filtered = useMemo(
    () =>
      transactions
        .filter((t) => {
          // Wykluczamy transakcje "Cel: ..." (deposit z portfela) — mają goalId
          if (t.goalId) return false;
          if (category) return t.categoryId === category.id;
          return !t.categoryId;
        })
        .sort((a, b) => b.date.getTime() - a.date.getTime()),
    [transactions, category],
  );

  // Suma pojedynczego znaku — w "Inne / brak kategorii" mogą być mixed type,
  // więc liczymy tylko absolute sum (transakcje już są w jednej kategorii).
  const total = filtered.reduce((s, t) => s + t.amount, 0);

  const memberBreakdown = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of filtered) {
      map.set(t.userId, (map.get(t.userId) ?? 0) + t.amount);
    }
    return [...map.entries()]
      .map(([uid, sum]) => ({
        uid,
        name: family?.members[uid]?.name ?? 'Nieznany',
        sum,
        pct: total > 0 ? Math.round((sum / total) * 100) : 0,
      }))
      .sort((a, b) => b.sum - a.sum);
  }, [filtered, family, total]);

  const Icon = getCategoryIcon(category?.icon);
  const color = safeColor(category?.color ?? '#94a3b8', '#94a3b8');

  return (
    <Modal
      onClose={onClose}
      size="lg"
      icon={
        <div
          className="w-16 h-16 rounded-full ring-8 flex items-center justify-center"
          style={{ backgroundColor: `${color}22`, color, boxShadow: `0 0 0 8px ${color}11` }}
        >
          <Icon size={28} strokeWidth={2.25} />
        </div>
      }
      title={category?.name ?? 'Inne / brak kategorii'}
      subtitle={`${filtered.length} ${filtered.length === 1 ? 'transakcja' : 'transakcji'} · ${formatCurrency(total)}`}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="w-full px-4 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-card transition"
        >
          Zamknij
        </button>
      }
    >
      <div className="space-y-4">
        {memberBreakdown.length > 1 && (
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
              Podział wśród rodziny
            </p>
            <ul className="space-y-1.5">
              {memberBreakdown.map((m) => (
                <li
                  key={m.uid}
                  className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/40"
                >
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{m.name}</span>
                      <span className="text-xs font-bold text-slate-400 tabular-nums">{m.pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${m.pct}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">
                    {formatCurrency(m.sum)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Transakcje
          </p>
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">
              Brak transakcji w tej kategorii
            </p>
          ) : (
            <div className="-mx-2">
              {filtered.map((tx) => (
                <TransactionRow
                  key={tx.id}
                  tx={tx}
                  category={category ?? undefined}
                  walletNamesById={walletNamesById}
                  onClick={() => onPickTx?.(tx)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
